import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getTranslations } from '../translations';

const PreferencesContext = createContext(undefined);

export const PreferencesProvider = ({ children }) => {
  // --- THEME STATE ---
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('admin-app_theme');
    if (saved) return saved === 'dark';
    // Check system preference if no saved preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // --- LANGUAGE STATE ---
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('admin-app_lang');
    return saved || 'SK'; // Default to Slovak
  });

  // Get translations for current language (memoized for performance)
  const translations = useMemo(() => {
    return getTranslations(language);
  }, [language]);

  // Apply Theme Side Effect
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('admin-app_theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('admin-app_theme', 'light');
    }
  }, [isDarkMode]);

  // Save Language Side Effect
  useEffect(() => {
    localStorage.setItem('admin-app_lang', language);
  }, [language]);

  const toggleTheme = () => setIsDarkMode(prev => !prev);

  // Explicitné nastavenie témy (onboarding wizard) — toggleTheme ostáva pre existujúce prepínače
  const setDarkMode = (value) => setIsDarkMode(Boolean(value));

  const setLanguage = (lang) => setLanguageState(lang);

  // Translation Helper with dot notation support
  // Example: t('sidebar.dashboard') or t('pages.employees.title')
  const t = (key) => {
    if (!key) return key;
    
    const keys = key.split('.');
    let current = translations;
    
    // Navigate through the nested object structure
    for (const k of keys) {
      if (current && typeof current === 'object' && k in current) {
        current = current[k];
      } else {
        // Fallback: return the key if translation not found
        console.warn(`Translation missing for key: ${key} (language: ${language})`);
        return key;
      }
    }
    
    // If we ended up with a string, return it, otherwise return the key
    return typeof current === 'string' ? current : key;
  };

  return (
    <PreferencesContext.Provider 
      value={{
        isDarkMode,
        toggleTheme,
        setDarkMode,
        language,
        setLanguage,
        t
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};

